// src/utils/tokenLogoUtils.js

// Fallback: coloured letter circle (accepts optional size)
export const getFallbackLogo = (symbol, size = 80) => {
  const letter = symbol?.[0] || "?";
  const fontSize = size * 0.55;
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Ccircle cx='${size/2}' cy='${size/2}' r='${size/2}' fill='%231673e6'/%3E%3Ctext x='${size/2}' y='${size/2 + fontSize*0.35}' font-size='${fontSize}' text-anchor='middle' fill='white' font-weight='bold'%3E${letter}%3C/text%3E%3C/svg%3E`;
};

// Helper to normalize chain names from IDs
const normalizeChain = (chainName) => {
  if (!chainName) return '';
  const lower = chainName.toString().toLowerCase();
  // GoldChain IDs
  if (lower === '0x777771' || lower === '777771') return 'goldchain';
  // Polygon IDs
  if (lower === '0x89' || lower === '137') return 'polygon';
  // Ethereum IDs
  if (lower === '0x1' || lower === '1') return 'ethereum';
  // BSC
  if (lower === '0x38' || lower === '56') return 'bsc';
  // Default: keep as is (assume already a name like 'polygon', 'ethereum', etc.)
  return lower;
};

// Token logo (hardcoded USDT, POL + TrustWallet fallback + wUSDT on GoldChain)
export const getTokenLogoUrl = (token, size = 80) => {
  // Normalize chain name (convert chain IDs to readable names)
  const normalizedChain = normalizeChain(token.chainName);

  console.log("getTokenLogoUrl called with:", { 
    symbol: token.symbol, 
    chainName: token.chainName, 
    normalizedChain 
  });

  // 1. SPECIAL CASE: wUSDT on GoldChain → Tether logo (highest priority)
  if (token.symbol?.toLowerCase() === "wusdt" && normalizedChain === "goldchain") {
    console.log("✅ Using Tether logo for wUSDT on GoldChain");
    return "https://cryptologos.cc/logos/tether-usdt-logo.png";
  }

  // 2. If a direct URL is provided (e.g., BAIT logo or DENO URL), use it
  if (token.logoUrl) return token.logoUrl;

  const addressLower = token.address?.toLowerCase();
  const hardcoded = {
    // USDT on Polygon
    "0xc2132d05d31c914a87c6611c10748aeb04b58e8f": "https://cryptologos.cc/logos/tether-usdt-logo.png",
    // POL native token (common address)
    "0x0000000000000000000000000000000000001010": "https://cryptologos.cc/logos/polygon-matic-logo.png",
  };
  if (addressLower && hardcoded[addressLower]) return hardcoded[addressLower];

  // 3. For ERC-20 tokens with a valid address, try Trust Wallet CDN
  if (token.address && token.address !== "0x0000000000000000000000000000000000001010" && token.address.length > 30) {
    const chainMap = { polygon: "polygon", ethereum: "ethereum", goldchain: "polygon" };
    const chain = chainMap[normalizedChain] || "ethereum";
    return `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/${chain}/assets/${token.address.toLowerCase()}/logo.png`;
  }

  // 4. Final fallback: coloured letter
  return getFallbackLogo(token.symbol, size);
};

// Chain logo – first tries local images (public/chain-logos/), then external CDN, then purple dot
export const getChainLogoUrl = (chainIdentifier) => {
  const id = (chainIdentifier || "").toString().toLowerCase();
  console.log("🔍 getChainLogoUrl received:", chainIdentifier, typeof chainIdentifier);
  
  // Local images (place inside public/chain-logos/)
  const localLogos = {
    polygon: "/chain-logos/polygon.png",
    ethereum: "/chain-logos/ethereum.png",
    goldchain: "/chain-logos/goldchainlogo.png",   // Your custom Goldchain logo
  };
  if (localLogos[id]) return localLogos[id];

  // External CDN fallback (maps chain names and chain IDs)
  const chainMap = {
    "polygon": "https://cryptologos.cc/logos/polygon-matic-logo.png",
    "137": "https://cryptologos.cc/logos/polygon-matic-logo.png",
    "0x89": "https://cryptologos.cc/logos/polygon-matic-logo.png",
    "ethereum": "https://cryptologos.cc/logos/ethereum-eth-logo.png",
    "1": "https://cryptologos.cc/logos/ethereum-eth-logo.png",
    "0x1": "https://cryptologos.cc/logos/ethereum-eth-logo.png",
    goldchain: "/chain-logos/goldchainlogo.png",
    "777771": "/chain-logos/goldchainlogo.png",
    "0x777771": "/chain-logos/goldchainlogo.png",
  };
  if (chainMap[id]) return chainMap[id];

  // Ultimate fallback: purple dot
  return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Ccircle cx='10' cy='10' r='10' fill='%238245E5'/%3E%3C/svg%3E";
};